import { Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { CostBucket } from '../interfaces/costs.interface';
import {
  costSharePercent,
  costToCsvDecimal,
  currencySymbol,
  formatApiDate,
  formatCost,
  formatTokens,
  sumCostStrings
} from './costs-format.util';
import { isAggregateTailKey, resolveBucketDisplay } from './costs-resource.util';

/**
 * CSV de uma dimensão do breakdown (a "tabela completa" de cada visão do painel).
 *
 * Mesmas três regras do CSV de execuções (`costs-csv.util.ts`), pelo mesmo motivo — o
 * destino é o Excel pt-BR: separador `;`, decimal com vírgula obtido por troca na
 * string, e UTF-8 **com BOM**, senão os acentos viram `Ã§`.
 *
 * A diferença em relação à tabela na tela: aqui os números saem CRUS (sem separador de
 * milhar e com toda a precisão do custo), porque numa planilha eles vão ser somados.
 * `US$ 147,96` e `47.719.399` são bons para ler e ruins para calcular.
 */

/** Marca de ordem de bytes. Vai como primeiro caractere do arquivo. */
const BOM = '\ufeff';

const SEPARATOR = ';';

/** O que a exportação precisa saber além das linhas — é o contexto da tabela de origem. */
export interface BreakdownCsvOptions {
  /** Rótulo da coluna-chave: "Área", "Usuário", "Review"... Vira o cabeçalho e o nome do arquivo. */
  keyLabel: string;
  currency: string;
  /** Dimensão `resource`: a chave vira duas colunas (tipo e identificador), como na tela. */
  splitResource?: boolean;
  /** Recorte da tela, para a primeira linha do arquivo e o nome dele. */
  period?: Period;
  /** Chaves reais da dimensão, antes do teto do backend. Só entra na nota de recorte. */
  totalKeys?: number;
  /** True quando a busca da tabela estava aplicada — o arquivo é o subconjunto filtrado. */
  filtered?: boolean;
}

/** Escapa uma célula: tudo entre aspas, com aspas internas duplicadas. */
function cell(value: string | number | undefined | null): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function headerRow(options: BreakdownCsvOptions): string {
  const symbol = currencySymbol(options.currency);
  return [
    ...(options.splitResource ? ['Tipo'] : []),
    options.keyLabel,
    symbol ? `Custo (${symbol})` : 'Custo',
    // Execuções ao lado do custo porque é o que revela reprocessamento; Fontes ao lado
    // de Execuções porque é o que separa "muitos arquivos" de "reprocessou muito".
    'Execuções',
    ...(options.splitResource ? ['Fontes'] : []),
    'Chamadas ao LLM',
    'Tokens'
  ].map(cell).join(SEPARATOR);
}

function dataRow(bucket: CostBucket, options: BreakdownCsvOptions): string {
  const display = resolveBucketDisplay(bucket, options.splitResource);
  return [
    ...(options.splitResource ? [cell(display.type ?? '')] : []),
    cell(display.key),
    // Troca de ponto por vírgula na string: preserva toda a precisão que veio da API.
    cell(costToCsvDecimal(bucket.cost)),
    cell(bucket.runs ?? 0),
    // Vazio, não zero, quando o backend não manda o campo (comparison/standalone são
    // sempre 1 arquivo): um "0" ali seria lido como "nenhum arquivo".
    ...(options.splitResource ? [cell(bucket.sourceCount ?? '')] : []),
    cell(bucket.llmCalls ?? 0),
    cell(bucket.tokens ?? 0)
  ].join(SEPARATOR);
}

/**
 * Nota de recorte, quando a última linha é a cauda somada pelo backend.
 *
 * Vai no arquivo pelo mesmo motivo que vai na tela: sem ela, um `(outros 1.234)` no
 * meio da planilha parece uma chave de verdade, e quem soma a coluna não tem como
 * saber que aquela linha representa mil outras.
 */
function cappedNote(rows: CostBucket[], options: BreakdownCsvOptions): string | null {
  // Menos de duas linhas: a busca da tabela deixou só a cauda passar (ela casa com
  // "outros"). Não há topo nenhum a descrever, e "exportadas as 0 chaves de maior
  // custo" seria pior que não dizer nada.
  if (rows.length < 2 || !isAggregateTailKey(rows[rows.length - 1].key)) { return null; }

  const shown = formatTokens(rows.length - 1);

  // Custo e fatia da cauda, como na tela: numa planilha o `(outros N)` é uma linha
  // igual às outras, e sem saber o peso dela quem filtra a coluna não tem como julgar
  // se o recorte compromete a análise.
  //
  // A fatia só sai quando o arquivo é a dimensão inteira: num subconjunto filtrado a
  // soma das linhas não é o custo do período, e o percentual passaria a medir a cauda
  // contra o próprio recorte — diria "100%" em qualquer busca que preservasse só ela.
  const tailCost = rows[rows.length - 1].cost;
  const share = options.filtered
    ? null
    : costSharePercent(tailCost, sumCostStrings(rows.map(bucket => bucket.cost)));
  const tail = `${formatCost(tailCost, options.currency)}${share ? `, ou ${share} do período` : ''}`;

  // Com busca aplicada, as linhas do arquivo não são o topo da dimensão nem fecham com
  // ela: o que a busca descartou não está listado NEM somado na cauda. Dizer "as N de
  // maior custo, de {total}" e "para o total continuar fechando" seria falso — o
  // arquivo já se declara filtrado numa linha própria, e aqui a cauda só se apresenta.
  if (options.filtered) {
    return `ATENÇÃO: a última linha (${tail}) é a cauda somada pelo backend, não uma chave. ` +
      `As chaves fora da busca não estão no arquivo nem nessa soma.`;
  }

  const total = options.totalKeys != null ? `, de ${formatTokens(options.totalKeys)}` : '';

  return `ATENÇÃO: exportadas as ${shown} chaves de maior custo${total}. ` +
    `As demais estão somadas na última linha (${tail}), para o total continuar fechando.`;
}

/** Monta o conteúdo completo do CSV (com BOM), pronto para virar Blob. */
export function buildBreakdownCsv(rows: CostBucket[], options: BreakdownCsvOptions): string {
  const buckets = rows ?? [];
  const period = options.period;
  const capped = cappedNote(buckets, options);

  const lines = [
    cell(`Custo por ${options.keyLabel.toLowerCase()}`),
    cell(period?.dateFrom && period?.dateTo
      ? `Período: ${formatApiDate(period.dateFrom)} a ${formatApiDate(period.dateTo)}`
      : 'Período: todo o histórico'),
    // A busca da tabela é parte do recorte exportado: sem dizer isso, o arquivo parece
    // a dimensão inteira e não fecha com o total do painel.
    ...(options.filtered ? [cell('Recorte: apenas as linhas que atendem à busca aplicada na tabela.')] : []),
    ...(capped ? [cell(capped)] : []),
    headerRow(options),
    ...buckets.map(bucket => dataRow(bucket, options))
  ];

  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** `área` → `area`: nome de arquivo sem acento, que nem todo sistema lida bem. */
function slug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * `custos-por-area-YYYY-MM-DD_a_YYYY-MM-DD.csv`. A dimensão entra no nome porque o
 * usuário baixa várias tabelas do mesmo período — sem ela os arquivos viriam como
 * `custos (1).csv`, `custos (2).csv`.
 */
export function breakdownCsvFilename(keyLabel: string, period?: Period): string {
  const dimension = slug(keyLabel) || 'dimensao';
  if (!period?.dateFrom || !period?.dateTo) { return `custos-por-${dimension}-completo.csv`; }
  return `custos-por-${dimension}-${period.dateFrom}_a_${period.dateTo}.csv`;
}
